import React, { useState, useEffect } from 'react'
import { Fade, Modal } from '@mui/material'
import View from '@modul-connect/shared/components/atoms/view'
import Button from '@modul-connect/shared/components/atoms/button'
import { H1, P } from '@modul-connect/shared/components/atoms/text'
import modal from '@modul-connect/shared/components/atoms/modal'
import theme from '@modul-connect/shared/theme'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth0 } from '@auth0/auth0-react'
import TableWidget from '@modul-connect/shared/components/molecules/tableWidget'
import { addVehicleTeam_columns } from './team_columns'
import { fetchVehicles } from '../../../state/actions/vehicles'
import { SearchField } from '@modul-connect/shared/components/molecules/searchWidget.v2/searchField'


export const AddTeamVehicleModal = ({ 
  openModal,
  onClose,
  addVehicle,
  addingFailed,
  disabled,
  vehicleAlreadyExists,
  subOrgId,
  parent
}) => {

  const [orgVehicles, setOrgVehicles] = useState('')
  const [selectedVehicles, setSelectedVehicles] = useState([])
  const [rowsOptions, setRowsOptions] = useState([])

  useEffect(() => {
    if (openModal) {
      setSelectedVehicles([])
    }
  }, [openModal])

  const vehicles = useSelector(state => state.vehicles)
  const nodeVehicles = useSelector(state => state.organisation_node.vehicles)
  const loading = useSelector(state => state.loading)

  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth0()

  const vehicles_prepared = vehicles ? vehicles.map(vehicle => ({
    ...vehicle,
    model: `${vehicle.vehicle.brandName} ${vehicle.vehicle.modelName}`,
  })) : []
  
  const onSave = () => {
    if (selectedVehicles.length === 0) {
      return
    }

    selectedVehicles.forEach(vehicle => {
      addVehicle({
        id: vehicle.boxId, 
        vehicleName: vehicle.vehicleName,
        model: vehicle.model,
        year: vehicle.year,
        licenseNumber: vehicle.licenseNumber,                
        organisation: subOrgId             
      })
    })
  
  }
  useEffect(() => {
    if (openModal) {
      getAccessTokenSilently().then(token => {
        dispatch(fetchVehicles(token, parent.uuid ))
      })
    }
  }, [openModal])
  

  const vehicleRows = vehicles_prepared.map(v => ({
    id: v.vehicle?.vin, 
    boxId: v.boxId,  
    vehicleName: v.name, 
    model: v.model, 
    year: v.vehicle.year,
    licenseNumber: v.vehicle?.licenseNumber ?? '—' 
  })).filter(vr => !nodeVehicles.find(nv => nv.id === vr.boxId)
  )


  const toggleSelectedRow = (row, prevSelected) => {
    const alreadySelected = prevSelected.find(v => v.id === row.id)
    return alreadySelected
      ? prevSelected.filter(v => v.id !== row.id)
      : [...prevSelected, row]
  }

  const handleNameChange = (event) => {
    setFilterAfterDelay(event.target.value)
  }

  let filterTimer = null

  const setFilterAfterDelay = (newFilter) => {
    if (filterTimer) {
      clearTimeout(filterTimer)
    }

    filterTimer = setTimeout(() => {
      setOrgVehicles(newFilter)
    }, 1000)
  }

  const sortByColumn = (data, column) => {
    if (!Array.isArray(data) || !column) return data

    if (typeof column !== 'string') column = ''
  
    const isDesc = column.endsWith('desc')
    const key = isDesc ? column.replace(/desc$/i, '') : column
  
    return [...data].sort((a, b) => {
      const v1 = a[key] || ''
      const v2 = b[key] || ''
  
      return isDesc
        ? v2.localeCompare(v1, undefined, { numeric: true })
        : v1.localeCompare(v2, undefined, { numeric: true })
    })
  }

  const filteredVehicles = sortByColumn(vehicleRows.filter(v =>
    v.vehicleName?.toLowerCase().includes(orgVehicles.toLowerCase()) ||
    v.boxId?.toLowerCase().includes(orgVehicles.toLowerCase()) ||
    v.id?.toLowerCase().includes(orgVehicles.toLowerCase()) ||
    v.modelName?.toLowerCase().includes(orgVehicles.toLowerCase()) ||
    v.brandName?.toLowerCase().includes(orgVehicles.toLowerCase()) ||
    v.licenseNumber?.toLowerCase().includes(orgVehicles.toLowerCase()) ||
    v.year?.toLowerCase().includes(orgVehicles.toLowerCase())
  ),
  rowsOptions?.sort || 'vehicleName'
  )
  

  const vehiclesToDisplay = !rowsOptions ? filteredVehicles : filteredVehicles.
    slice(rowsOptions.rowsPerPage * (rowsOptions.currentpage -1), filteredVehicles.length)
  

  return (
    <Modal
      open={openModal}
      onClose={(event, reason) => {
        if (disabled && reason === 'backdropClick') return
        onClose()
      }}
    >
      <Fade in={openModal}>
        <div>
          <View   extend={{
            ...modal.container,
            width: '70%',
            height: '80%',
            position: 'absolute',
            top: '14%',
            left: '50%',
            transform: 'translateX(-50%)',
            '@media (max-width: 1025px)': {
              width: '94%',
              left: '50%',
              right: 'auto',     
              transform: 'translateX(-50%)',
            },
  
          }}>
            <View extend={modal.header}>
              <H1 text="Add vehicle to team" />
            </View>
            <View extend={{
              ...modal.content,
              maxHeight: '60vh',      
              overflowY: 'auto',
            }}>
              {addingFailed && (
                <P large style={{ color: theme.colors.error }}>
                  Adding vehicle failed. (Server error)
                </P>
              )}

              {vehicleAlreadyExists && (
                <P large style={{ color: theme.colors.error }}>
                  Vehicle already added.
                </P>
              )}
          
              <SearchField
                extend={{ backgroundColor: theme.colors.lightGrey }}
                onChange={handleNameChange}
                placeholder={'Search vehicle...'}
                initialValue={orgVehicles}
              />

              <TableWidget
                data={vehiclesToDisplay}
                columns={addVehicleTeam_columns}
                totalItems={vehicleRows.length}
                defaultSort="vehicleName"
                searchStr={''}
                themes={{ device: 'desktop' }}
                extend={{ paddingTop: 10 }}
                loadingStatus={loading?.fetchVehicles}
                selectedRows={selectedVehicles.map(v => v.id)}
                onClickRow={(event, id, row) => {
                  setSelectedVehicles(prev => toggleSelectedRow(row, prev))
                }}
                onFetchData={(options) => {
                  setRowsOptions(options)
                }}  
              />
            </View>
            <View extend={modal.footer}>
              <Button noBg disabled={disabled} cancel onClick={onClose}>
                Cancel
              </Button>
              <Button noBg disabled={disabled || vehicleAlreadyExists || selectedVehicles.length === 0} noPadding onClick={onSave}>
                Submit ({selectedVehicles.length})
              </Button>
            </View>
          </View>

        </div>
      </Fade>
    </Modal>
  )
}

export default AddTeamVehicleModal
